import React from "react";
import { Box, Chip, LinearProgress, Paper, Stack, Typography } from "@mui/material";

export const KPI_ACCENTS = {
  revenue: {
    bgcolor: "#1d3a62",
    color: "#fff",
    muted: "rgba(255,255,255,0.85)",
    meterTrack: "rgba(255,255,255,0.22)",
    meterBar: "#86efac",
    infoTone: "onDark",
  },
  throughput: {
    bgcolor: "#0f69a7",
    color: "#fff",
    muted: "rgba(255,255,255,0.85)",
    meterTrack: "rgba(255,255,255,0.22)",
    meterBar: "#86efac",
    infoTone: "onDark",
  },
  fleet: {
    bgcolor: "#15b9bb",
    color: "#fff",
    muted: "rgba(255,255,255,0.85)",
    meterTrack: "rgba(255,255,255,0.22)",
    meterBar: "#ecfccb",
    infoTone: "onDark",
  },
  performance: {
    bgcolor: "#1e4d7b",
    color: "#fff",
    muted: "rgba(255,255,255,0.85)",
    meterTrack: "rgba(255,255,255,0.22)",
    meterBar: "#38bdf8",
    infoTone: "onDark",
    good: "#86efac",
    bad: "#fecaca",
  },
  pms: {
    bgcolor: "#a1d0a2",
    color: "#1a3328",
    muted: "#2d4a3e",
    meterTrack: "rgba(30,58,47,0.18)",
    meterBar: "#1a3328",
    infoTone: "onLight",
    good: "#14532d",
    bad: "#991b1b",
  },
  outstanding: {
    bgcolor: "#f7af5d",
    color: "#7c2d12",
    muted: "#92400e",
    meterTrack: "rgba(120,53,15,0.18)",
    meterBar: "#7c2d12",
    infoTone: "onLight",
  },
  risk: {
    bgcolor: "#fbe9d5",
    color: "#7c2d12",
    muted: "#9a3412",
    meterTrack: "rgba(247,175,93,0.45)",
    meterBar: "linear-gradient(90deg, #f7af5d, #ef4444)",
    infoTone: "onLight",
  },
};

const PILL_STYLES = {
  good: { bgcolor: "rgba(134,239,172,0.22)", color: "#bbf7d0" },
  bad: { bgcolor: "rgba(254,202,202,0.22)", color: "#fecaca" },
  warn: { bgcolor: "rgba(253,230,138,0.22)", color: "#fde68a" },
  neutral: { bgcolor: "rgba(255,255,255,0.16)", color: "rgba(255,255,255,0.9)" },
  goodOnLight: { bgcolor: "rgba(22,163,74,0.15)", color: "#14532d" },
  badOnLight: { bgcolor: "rgba(220,38,38,0.12)", color: "#991b1b" },
  warnOnLight: { bgcolor: "rgba(217,138,6,0.15)", color: "#92400e" },
  neutralOnLight: { bgcolor: "rgba(22,26,58,0.08)", color: "#161a3a" },
};

export const getKpiTheme = (accent = "revenue") =>
  KPI_ACCENTS[accent] || KPI_ACCENTS.revenue;

export const KpiCardShell = ({ accent = "revenue", children, sx = {} }) => {
  const theme = getKpiTheme(accent);
  return (
    <Paper
      elevation={0}
      component="article"
      sx={{
        bgcolor: theme.bgcolor,
        color: theme.color,
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        minHeight: 118,
        minWidth: 0,
        // visible so info tooltips are not clipped by the card shell
        overflow: "visible",
        position: "relative",
        zIndex: 1,
        "&:hover, &:focus-within": { zIndex: 40 },
        p: "12px 14px",
        width: "100%",
        // Keep dirham/riyal symbol size equal to surrounding text on KPI cards
        "& .currency-symbol": {
          fontSize: "1em",
          lineHeight: 1,
          marginLeft: "4px",
          marginRight: 0,
          verticalAlign: "baseline",
        },
        "& .currency-symbol .dirham-symbol": {
          fontSize: "1em",
        },
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

export const KpiCardTitle = ({ children, sx = {} }) => (
  <Typography
    component="h3"
    noWrap
    sx={{
      flex: 1,
      fontSize: 12.5,
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: 1.25,
      minWidth: 0,
      m: 0,
      opacity: 0.92,
      pr: 0.5,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const KpiCardValue = ({ children, tone, theme, sx = {} }) => (
  <Typography
    component="p"
    sx={{
      fontFamily: '"Open Sans", Roboto, Helvetica, Arial, sans-serif',
      fontSize: 24,
      fontVariantNumeric: "tabular-nums",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.15,
      m: 0,
      color:
        tone === "good"
          ? theme?.good || "#86efac"
          : tone === "bad"
            ? theme?.bad || "#fecaca"
            : "inherit",
      // Currency/dirham must match the value size
      "& small": {
        fontSize: "1em",
        fontWeight: "inherit",
        ml: 0.5,
        opacity: 1,
        verticalAlign: "baseline",
      },
      "& .currency-symbol, & small .currency-symbol, & .dirham-symbol": {
        fontSize: "1em",
        fontWeight: "inherit",
        marginLeft: "4px",
        marginRight: 0,
        verticalAlign: "baseline",
      },
      ...sx,
    }}
  >
    {children}
  </Typography>
);

export const KpiMeter = ({ value, theme }) => (
  <LinearProgress
    variant="determinate"
    value={Math.min(Math.max(Number(value) || 0, 0), 100)}
    sx={{
      bgcolor: theme.meterTrack,
      borderRadius: 99,
      height: 5,
      overflow: "hidden",
      "& .MuiLinearProgress-bar": {
        bgcolor: theme.meterBar.includes("gradient")
          ? "transparent"
          : theme.meterBar,
        backgroundImage: theme.meterBar.includes("gradient")
          ? theme.meterBar
          : "none",
        borderRadius: 99,
      },
    }}
  />
);

export const KpiSubRow = ({ children, sx = {} }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    flexWrap="wrap"
    useFlexGap
    spacing={0.75}
    sx={{
      fontSize: 11.5,
      gap: 0.75,
      opacity: 0.95,
      width: "100%",
      "& b": { fontWeight: 700, opacity: 0.95 },
      "& .currency-symbol": {
        fontSize: "1em",
        marginLeft: "3px",
        marginRight: 0,
      },
      ...sx,
    }}
  >
    {children}
  </Stack>
);

export const KpiPill = ({ variant = "neutral", onDark = true, children }) => {
  const key = onDark ? variant : `${variant}OnLight`;
  const style = PILL_STYLES[key] || PILL_STYLES[variant] || PILL_STYLES.neutral;
  return (
    <Chip
      size="small"
      label={children}
      sx={{
        bgcolor: style.bgcolor,
        color: style.color,
        fontSize: 11,
        fontWeight: 700,
        height: 22,
        borderRadius: "99px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

export const KpiHeader = ({ title, info }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    sx={{ minHeight: 24, width: "100%" }}
  >
    <KpiCardTitle>{title}</KpiCardTitle>
    {info}
  </Stack>
);

export const KpiSkeleton = () => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: "#fff",
      border: "1px solid #e3e5ee",
      borderRadius: "10px",
      minHeight: 118,
      p: "12px 14px",
      width: "100%",
    }}
  >
    <Stack spacing={1.25}>
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 14, width: "55%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 28, width: "40%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 99, height: 5, width: "100%" }} />
      <Box sx={{ bgcolor: "#eef0f6", borderRadius: 1, height: 12, width: "70%" }} />
    </Stack>
  </Paper>
);
